import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';

import {Permissions} from 'auth/PermissionConstants';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import * as RegistryConstants from 'constants/RegistryConstants';
import * as SampleConstants from 'constants/SampleConstants';
import * as FileConstants from 'folder-items/FileConstants';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {requestFetchManagedModel} from 'models/ManagedModelUtil';
import {BenchlingState} from 'redux-stores/reduceApp';
import TransferModal from 'samples/transfers/TransferModal';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import ModalAnchor from 'ui-library/ModalAnchor';

import ContainableStorageTables from './ContainableStorageTables';
import StorableStore, {ContainableContainerListing, ContainablePlateListing} from './StorableStore';
import * as InputSampleConstants from './transfers/input-sample/InputSampleConstants';

interface StorableStoreProps {
  loading: boolean;
  containerListings: {
    [listingId: string]: ContainableContainerListing;
  };
  plateListings: {
    [listingId: string]: ContainablePlateListing;
  };
}

function mapStorableStoreToProps(
  storableStore: typeof StorableStore,
  {bioentityId}: BaseBioentityStorablesViewProps & ConnectedProps
): StorableStoreProps {
  const containerListings = StorableStore.getContainableContainerListings();
  const plateListings = StorableStore.getContainablePlateListings();
  return {
    loading: !(containerListings && plateListings),
    containerListings,
    plateListings,
  };
}

interface ConnectedProps {
  bioentitySummary: FileConstants.BioentitySummary | null | undefined;
}

function mapStateToProps(
  state: BenchlingState,
  {bioentityId}: BaseBioentityStorablesViewProps
): ConnectedProps {
  return {
    bioentitySummary: state.models.fileSummariesById[bioentityId],
  };
}

interface BaseBioentityStorablesViewProps {
  bioentityId: string;
  permissions: Permissions;
  tagSchema: RegistryConstants.TagSchema;
}

type BioentityStorablesViewProps = BaseBioentityStorablesViewProps & ConnectedProps & StorableStoreProps;

interface State {
  isTransferModalOpen: boolean;
}

/**
 * Component displaying containers linked to a bioentity.
 */
class BioentityStorablesView extends React.Component<BioentityStorablesViewProps, State> {
  state: State = {isTransferModalOpen: false};

  componentDidMount(): void {
    requestFetchManagedModel(ManagedModelType.BIOENTITY_SUMMARY, this.props.bioentityId);
  }

  private handleOpenTransferModal = (): void => {
    this.setState({isTransferModalOpen: true});
  };

  private handleCloseTransferModal = (): void => {
    this.setState({isTransferModalOpen: false});
  };

  render(): JSX.Element {
    if (this.props.loading) {
      return (
        <div className="react-tab-container">
          <div className="u-spacing-top text-center">
            <HorizontalThrobber />
          </div>
        </div>
      );
    }

    return (
      <div className="react-tab-container storageView">
        <ModalAnchor open={this.state.isTransferModalOpen}>
          <TransferModal
            flow={SampleConstants.TransferFlow.TRANSFER_OUT}
            onRequestClose={this.handleCloseTransferModal}
            registryId={this.props.tagSchema.registryId}
            title="Add to containers"
            preselectedInputConfig={{
              inputType: InputSampleConstants.TYPE.EXISTING_BIOENTITY,
              inputBioentity: this.props.bioentitySummary,
            }}
            actionOrigin={ActionOrigin.REGISTRY_ITEM_PAGE_STORAGE_TAB}
          />
        </ModalAnchor>
        <Tooltip title="Add to containers">
          <Button
            buttonStyle={ButtonStyle.SECONDARY}
            className="u-no-wrap"
            disabled={!this.props.bioentitySummary}
            icon="transfer"
            onClick={this.handleOpenTransferModal}
            size={ButtonSize.SMALL}
          />
        </Tooltip>
        <ContainableStorageTables
          containableId={this.props.bioentityId}
          containerListings={this.props.containerListings}
          isReadOnly={!this.props.permissions.appendable}
          plateListings={this.props.plateListings}
        />
      </div>
    );
  }
}
export {BioentityStorablesView as UnwrappedBioentityStorablesView};

export default compose(
  connect(mapStateToProps),
  StorableStore.connect(mapStorableStoreToProps)
)(BioentityStorablesView);
